// Import Files Tool - Provides selective file import for workspace
// Extends WorkspaceTool - has access to appType, toolSystem, fs, and git

import { WorkspaceTool } from "../../base/WorkspaceTool.js";
import { logger } from "../../../../../../core/logger.js";

export class ImportFilesTool extends WorkspaceTool {
  constructor() {
    super();
    this.toolName = "importFilesTool";
  }

  /**
   * Analyze import to detect conflicts without writing files
   * @param {Array} files - Array of file objects: { relativePath, content, encoding }
   * @param {string} targetPath - Destination folder in workspace
   * @returns {Promise<Object>} Result with conflicts and normalized plan
   */
  async analyzeImport(files = [], targetPath = "/") {
    try {
      if (!Array.isArray(files) || files.length === 0) {
        return this.returnError({}, "Files array is required to analyze import");
      }

      if (this.appType === "browser") {
        return await this.analyzeImportForBrowser(files, targetPath);
      }
      if (this.appType === "local") {
        return await this.analyzeImportForLocal(files, targetPath);
      }

      return this.returnError(
        {},
        `App type '${this.appType}' not yet implemented for analyzeImport`
      );
    } catch (error) {
      logger.error(
        { tags: "tools|importfiles|analyze|error", color1: "red" },
        "Failed to analyze import",
        error
      );
      return this.returnError({}, error.message);
    }
  }

  /**
   * Import files into Lightning FS after resolving conflicts
   * @param {Array} files - Array of file objects: { relativePath, content, encoding }
   * @param {string} targetPath - Destination folder in workspace
   * @param {Object} resolution - Conflict resolution options
  * @param {Function} [onProgress] - Optional callback (current, total) for progress updates
  * @returns {Promise<Object>} Result with import summary
  */

  async importFiles(files = [], targetPath = "/", resolution = {}, onProgress = null) {
    try {
      if (!Array.isArray(files) || files.length === 0) {
        return this.returnError({}, "Files array is required to import files");
      }

      if (this.appType === "browser") {
        return await this.importFilesForBrowser(files, targetPath, resolution, onProgress);
      }
      if (this.appType === "local") {
        return await this.importFilesForLocal(files, targetPath, resolution, onProgress);
      }

      return this.returnError(
        {},
        `App type '${this.appType}' not yet implemented for importFiles`
      );
    } catch (error) {
      logger.error(
        { tags: "tools|importfiles|error", color1: "red" },
        "Failed to import files",
        error
      );
      return this.returnError({}, error.message);
    }
  }

  async analyzeImportForBrowser(files, targetPath) {
    const fs = this.fs?.promises;
    if (!fs) {
      return this.returnError(
        {},
        "Lightning FS not available - browser app filesystem not initialized"
      );
    }

    const normalizedTargetPath = this.normalizeTargetPath(targetPath);
    if (!normalizedTargetPath) {
      return this.returnError({}, "Invalid target path");
    }

    const conflicts = [];
    const normalizedFiles = [];
    const seenPaths = new Set();
    const importPathsSet = new Set();

    for (const file of files) {
      const relativePath = this.normalizeRelativePath(file?.relativePath);
      if (!relativePath) {
        continue;
      }

      const destinationPath = this.joinPaths(normalizedTargetPath, relativePath);

      if (seenPaths.has(destinationPath)) {
        conflicts.push({
          path: destinationPath,
          reason: "duplicate",
        });
        continue;
      }

      seenPaths.add(destinationPath);
      importPathsSet.add(destinationPath);

      const exists = await this.pathExists(`/files${destinationPath}`, fs);
      if (exists) {
        conflicts.push({
          path: destinationPath,
          reason: "exists",
        });
      }

      normalizedFiles.push({
        relativePath,
        destinationPath,
        content: file.content,
        encoding: file.encoding || "utf8",
      });
    }

    // Compute removals: workspace files not in import (excluding .git - handled separately)
    const removals = await this.computeRemovals(fs, importPathsSet);

    // Compute git comparison info
    const gitInfo = await this.computeGitInfo(files, fs);

    return this.returnSuccess(
      {
        targetPath: normalizedTargetPath,
        files: normalizedFiles,
        conflicts,
        removals,
        gitInfo,
        totalFiles: normalizedFiles.length,
      },
      `Analyzed ${normalizedFiles.length} file(s) for import`
    );
  }

  async computeRemovals(fs, importPathsSet) {
    const removals = [];
    const listTool = this.toolSystem?.listDirectoryTool;
    if (!listTool) return removals;

    const listResult = await listTool.listDirectory("/", true, false, true);
    if (!listResult?.success) return removals;

    const entries = listResult.data?.entries || [];
    for (const entry of entries) {
      if (entry.type !== "file") continue;
      const path = entry.path;
      if (!path || path.startsWith("/.git")) continue;
      if (importPathsSet.has(path)) continue;
      removals.push({ path, reason: "notInImport" });
    }
    return removals;
  }

  async computeGitInfo(importFiles, fs) {
    const gitInfo = {
      currentHasGit: false,
      currentCommitCount: 0,
      importHasGit: false,
      importCommitCount: 0,
    };

    const currentGitExists = await this.pathExists("/files/.git", fs);
    gitInfo.currentHasGit = !!currentGitExists;

    if (currentGitExists) {
      try {
        const logTool = this.toolSystem?.gitLogTool;
        if (logTool) {
          const logResult = await logTool.gitLog(9999);
          if (logResult?.success && logResult.data?.commits) {
            gitInfo.currentCommitCount = logResult.data.commits.length;
          }
        }
      } catch (error) {
        logger.warn(
          { tags: "tools|importfiles|git", color1: "yellow" },
          "Could not get current git commit count:",
          error?.message
        );
      }
    }

    const importPaths = importFiles.map((f) => this.normalizeRelativePath(f?.relativePath)).filter(Boolean);
    const hasGitInImport = importPaths.some((p) => p.startsWith(".git/") || p === ".git");
    gitInfo.importHasGit = hasGitInImport;

    if (hasGitInImport) {
      const headLogFile = importFiles.find((f) => {
        const rp = this.normalizeRelativePath(f?.relativePath) || "";
        return rp === ".git/logs/HEAD" || rp.replace(/^\/+/, "").endsWith(".git/logs/HEAD");
      });
      if (headLogFile?.content && typeof headLogFile.content === "string") {
        const lines = headLogFile.content.trim().split("\n").filter((l) => l.length > 0);
        gitInfo.importCommitCount = lines.length;
      }
    }

    return gitInfo;
  }

  async importFilesForBrowser(files, targetPath, resolution, onProgress = null) {
    const fs = this.fs?.promises;
    if (!fs) {
      return this.returnError(
        {},
        "Lightning FS not available - browser app filesystem not initialized"
      );
    }

    const normalizedTargetPath = this.normalizeTargetPath(targetPath);
    if (!normalizedTargetPath) {
      return this.returnError({}, "Invalid target path");
    }

    const defaultAction = resolution?.defaultAction || "replace";
    const perFile = resolution?.perFile || {};

    const summary = {
      total: 0,
      imported: 0,
      skipped: 0,
      renamed: 0,
      replaced: 0,
      results: [],
    };

    for (const file of files) {
      const relativePath = this.normalizeRelativePath(file?.relativePath);
      if (!relativePath) {
        continue;
      }

      summary.total += 1;

      const destinationPath = this.joinPaths(normalizedTargetPath, relativePath);
      const filesystemPath = `/files${destinationPath}`;
      const exists = await this.pathExists(filesystemPath, fs);

      const action = perFile[destinationPath] || defaultAction;
      let finalPath = destinationPath;
      let shouldWrite = true;

      if (exists) {
        if (action === "keep") {
          summary.skipped += 1;
          summary.results.push({ path: destinationPath, action: "skipped" });
          shouldWrite = false;
        } else if (action === "rename") {
          finalPath = await this.findAvailableName(destinationPath, fs);
          summary.renamed += 1;
        } else {
          summary.replaced += 1;
        }
      }

      if (!shouldWrite) {
        if (typeof onProgress === "function") {
          onProgress(summary.skipped + summary.imported + summary.results.filter((r) => r.action === "failed").length, files.length);
        }
        continue;
      }

      const finalFilesystemPath = `/files${finalPath}`;

      try {
        const dirPath = finalFilesystemPath.split("/").slice(0, -1).join("/") || "/";
        if (dirPath && dirPath !== "/") {
          await this.ensureDirectoryForImport(dirPath, fs);
        }

        let dataToWrite;
        const encoding = file.encoding || "utf8";
        if (encoding === "base64") {
          dataToWrite = this.decodeBase64ForImport(file.content);
        } else {
          dataToWrite = file.content;
        }

        await fs.writeFile(finalFilesystemPath, dataToWrite);

        summary.imported += 1;
        summary.results.push({ path: finalPath, action: "imported" });
        if (typeof onProgress === "function") {
          onProgress(summary.imported + summary.skipped + summary.results.filter((r) => r.action === "failed").length, files.length);
        }
      } catch (error) {
        logger.error(
          { tags: "tools|importfiles|write", color1: "red" },
          `Failed to write file: ${finalPath}`,
          error
        );
        summary.results.push({
          path: finalPath,
          action: "failed",
          error: error.message,
        });
        if (typeof onProgress === "function") {
          onProgress(summary.imported + summary.skipped + summary.results.filter((r) => r.action === "failed").length, files.length);
        }
      }
    }

    return this.returnSuccess(summary, `Imported ${summary.imported} file(s)`);
  }

  async ensureDirectoryForImport(directoryPath, fs) {
    if (!directoryPath || directoryPath === "/") return;
    const segments = directoryPath.split("/").filter(Boolean);
    let currentPath = "";
    for (const segment of segments) {
      currentPath += `/${segment}`;
      try {
        await fs.stat(currentPath);
      } catch (error) {
        if (error.code === "ENOENT") {
          try {
            await fs.mkdir(currentPath);
          } catch (mkdirError) {
            if (mkdirError.code !== "EEXIST") throw mkdirError;
          }
        } else {
          throw error;
        }
      }
    }
  }

  decodeBase64ForImport(base64String) {
    if (typeof atob === "function") {
      const binaryString = atob(base64String);
      const len = binaryString.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      return bytes;
    }
    if (typeof Buffer !== "undefined") {
      return Uint8Array.from(Buffer.from(base64String, "base64"));
    }
    throw new Error("Unable to decode base64 content");
  }

  normalizeTargetPath(targetPath = "/") {
    if (typeof targetPath !== "string") return null;
    const trimmed = targetPath.trim();
    if (!trimmed) return "/";

    let normalized = trimmed.replace(/\\/g, "/");
    normalized = normalized.replace(/^\/+/, "");

    if (normalized.includes("..")) return null;
    return normalized === "" ? "/" : `/${normalized.replace(/\/+$/, "")}`;
  }

  normalizeRelativePath(path) {
    if (typeof path !== "string") return null;
    const trimmed = path.trim();
    if (!trimmed) return null;

    let normalized = trimmed.replace(/\\/g, "/");
    normalized = normalized.replace(/^\/+/, "");

    if (normalized.includes("..")) return null;
    return normalized;
  }

  joinPaths(basePath, relativePath) {
    if (basePath === "/") return `/${relativePath}`;
    return `${basePath}/${relativePath}`.replace(/\/+/g, "/");
  }

  async pathExists(filesystemPath, fs) {
    try {
      await fs.stat(filesystemPath);
      return true;
    } catch (error) {
      if (error.code === "ENOENT") return false;
      throw error;
    }
  }

  async findAvailableName(destinationPath, fs) {
    const { dir, base, ext } = this.splitPath(destinationPath);
    let counter = 1;

    while (counter < 9999) {
      const candidate = `${dir}/${base} (${counter})${ext}`.replace(/\/+/g, "/");
      const exists = await this.pathExists(`/files${candidate}`, fs);
      if (!exists) {
        return candidate;
      }
      counter += 1;
    }

    return destinationPath;
  }

  splitPath(path) {
    const lastSlash = path.lastIndexOf("/");
    const dir = lastSlash > 0 ? path.slice(0, lastSlash) : "";
    const fileName = lastSlash >= 0 ? path.slice(lastSlash + 1) : path;
    const dotIndex = fileName.lastIndexOf(".");

    if (dotIndex <= 0) {
      return { dir: dir || "", base: fileName, ext: "" };
    }

    return {
      dir: dir || "",
      base: fileName.slice(0, dotIndex),
      ext: fileName.slice(dotIndex),
    };
  }

  /**
   * Analyze import against the native working directory (Revolt Desktop IPC).
   * Mirrors the browser flow but uses workspace-relative paths (no `/files` prefix)
   * and delegates existence / removal / git checks to the already-local-aware tools.
   */
  async analyzeImportForLocal(files, targetPath) {
    const readTool = this.toolSystem?.readFileTool;
    if (!readTool) {
      return this.returnError({}, "ReadFileTool is not available");
    }

    const normalizedTargetPath = this.normalizeTargetPath(targetPath);
    if (!normalizedTargetPath) {
      return this.returnError({}, "Invalid target path");
    }

    const conflicts = [];
    const normalizedFiles = [];
    const seenPaths = new Set();
    const importPathsSet = new Set();

    for (const file of files) {
      const relativePath = this.normalizeRelativePath(file?.relativePath);
      if (!relativePath) continue;

      const destinationPath = this.joinPaths(normalizedTargetPath, relativePath);

      if (seenPaths.has(destinationPath)) {
        conflicts.push({ path: destinationPath, reason: "duplicate" });
        continue;
      }

      seenPaths.add(destinationPath);
      importPathsSet.add(destinationPath);

      const exists = await readTool.checkExists(destinationPath);
      if (exists) {
        conflicts.push({ path: destinationPath, reason: "exists" });
      }

      normalizedFiles.push({
        relativePath,
        destinationPath,
        content: file.content,
        encoding: file.encoding || "utf8",
      });
    }

    const removals = await this.computeRemovalsForLocal(importPathsSet);
    const gitInfo = await this.computeGitInfoForLocal(files);

    return this.returnSuccess(
      {
        targetPath: normalizedTargetPath,
        files: normalizedFiles,
        conflicts,
        removals,
        gitInfo,
        totalFiles: normalizedFiles.length,
      },
      `Analyzed ${normalizedFiles.length} file(s) for import`
    );
  }

  async computeRemovalsForLocal(importPathsSet) {
    const removals = [];
    const listTool = this.toolSystem?.listDirectoryTool;
    if (!listTool) return removals;

    const listResult = await listTool.listDirectory("/", true, false, true);
    if (!listResult?.success) return removals;

    const entries = listResult.data?.entries || [];
    for (const entry of entries) {
      if (entry.type !== "file") continue;
      const path = entry.path;
      if (!path || path.startsWith("/.git")) continue;
      if (importPathsSet.has(path)) continue;
      removals.push({ path, reason: "notInImport" });
    }
    return removals;
  }

  async computeGitInfoForLocal(importFiles) {
    const gitInfo = {
      currentHasGit: false,
      currentCommitCount: 0,
      importHasGit: false,
      importCommitCount: 0,
    };

    const readTool = this.toolSystem?.readFileTool;
    if (readTool) {
      gitInfo.currentHasGit = !!(await readTool.checkExists("/.git"));
    }

    if (gitInfo.currentHasGit) {
      try {
        const logTool = this.toolSystem?.gitLogTool;
        if (logTool) {
          const logResult = await logTool.gitLog(9999);
          if (logResult?.success && logResult.data?.commits) {
            gitInfo.currentCommitCount = logResult.data.commits.length;
          }
        }
      } catch (error) {
        logger.warn(
          { tags: "tools|importfiles|git|local", color1: "yellow" },
          "Could not get current git commit count (local):",
          error?.message
        );
      }
    }

    const importPaths = importFiles
      .map((f) => this.normalizeRelativePath(f?.relativePath))
      .filter(Boolean);
    const hasGitInImport = importPaths.some(
      (p) => p.startsWith(".git/") || p === ".git"
    );
    gitInfo.importHasGit = hasGitInImport;

    if (hasGitInImport) {
      const headLogFile = importFiles.find((f) => {
        const rp = this.normalizeRelativePath(f?.relativePath) || "";
        return (
          rp === ".git/logs/HEAD" ||
          rp.replace(/^\/+/, "").endsWith(".git/logs/HEAD")
        );
      });
      if (headLogFile?.content && typeof headLogFile.content === "string") {
        const lines = headLogFile.content
          .trim()
          .split("\n")
          .filter((l) => l.length > 0);
        gitInfo.importCommitCount = lines.length;
      }
    }

    return gitInfo;
  }

  /**
   * Import files into the native working directory (Revolt Desktop IPC).
   * Delegates to `WriteFileTool.writeFile` which already handles local IPC
   * and utf8 / base64 payloads + createDirectories.
   */
  async importFilesForLocal(files, targetPath, resolution, onProgress = null) {
    const writeTool = this.toolSystem?.writeFileTool;
    const readTool = this.toolSystem?.readFileTool;
    if (!writeTool || !readTool) {
      return this.returnError(
        {},
        "WriteFileTool or ReadFileTool is not available"
      );
    }

    const normalizedTargetPath = this.normalizeTargetPath(targetPath);
    if (!normalizedTargetPath) {
      return this.returnError({}, "Invalid target path");
    }

    const defaultAction = resolution?.defaultAction || "replace";
    const perFile = resolution?.perFile || {};

    const summary = {
      total: 0,
      imported: 0,
      skipped: 0,
      renamed: 0,
      replaced: 0,
      results: [],
    };

    const reportProgress = () => {
      if (typeof onProgress !== "function") return;
      const failed = summary.results.filter((r) => r.action === "failed").length;
      onProgress(summary.imported + summary.skipped + failed, files.length);
    };

    for (const file of files) {
      const relativePath = this.normalizeRelativePath(file?.relativePath);
      if (!relativePath) continue;

      summary.total += 1;

      const destinationPath = this.joinPaths(normalizedTargetPath, relativePath);
      const exists = await readTool.checkExists(destinationPath);

      const action = perFile[destinationPath] || defaultAction;
      let finalPath = destinationPath;
      let shouldWrite = true;

      if (exists) {
        if (action === "keep") {
          summary.skipped += 1;
          summary.results.push({ path: destinationPath, action: "skipped" });
          shouldWrite = false;
        } else if (action === "rename") {
          finalPath = await this.findAvailableNameForLocal(destinationPath, readTool);
          summary.renamed += 1;
        } else {
          summary.replaced += 1;
        }
      }

      if (!shouldWrite) {
        reportProgress();
        continue;
      }

      try {
        const encoding = file.encoding === "base64" ? "base64" : "utf8";
        const writeResult = await writeTool.writeFile(
          finalPath,
          file.content,
          true,
          encoding,
          true
        );
        if (!writeResult?.success) {
          throw new Error(
            writeResult?.message || `Failed to write ${finalPath}`
          );
        }

        summary.imported += 1;
        summary.results.push({ path: finalPath, action: "imported" });
        reportProgress();
      } catch (error) {
        logger.error(
          { tags: "tools|importfiles|write|local", color1: "red" },
          `Failed to write file (local): ${finalPath}`,
          error
        );
        summary.results.push({
          path: finalPath,
          action: "failed",
          error: error.message,
        });
        reportProgress();
      }
    }

    return this.returnSuccess(summary, `Imported ${summary.imported} file(s)`);
  }

  /**
   * Find an available `name (N).ext` variant on disk for local apps.
   */
  async findAvailableNameForLocal(destinationPath, readTool) {
    const { dir, base, ext } = this.splitPath(destinationPath);
    let counter = 1;
    while (counter < 9999) {
      const candidate = `${dir}/${base} (${counter})${ext}`.replace(/\/+/g, "/");
      const exists = await readTool.checkExists(candidate);
      if (!exists) return candidate;
      counter += 1;
    }
    return destinationPath;
  }
}
